import { useEffect, useLayoutEffect, useState } from 'react'
import { captureException } from '@sentry/react'
import {
  Button,
  Field,
  FormLabel,
  Heading,
  Paragraph,
  Popup,
  showToast,
  Textarea,
  TriangleExclamationIcon,
} from '@quickmd-llc/design-system'
import {
  CANCEL_TEXT,
  DELETE_MEDICAL_RECORD_REASON_LABEL_TEXT,
  DELETE_NOTE_CONFIRMATION_TEXT,
  DELETE_NOTE_TITLE_TEXT,
  DELETE_PATIENT_INSTRUCTION_CONFIRMATION_TEXT,
  DELETE_PATIENT_INSTRUCTION_TITLE_TEXT,
  DELETE_TEXT,
  NOTE_DELETED_TEXT,
  PATIENT_INSTRUCTION_DELETED_TEXT,
  TYPE_SOMETHING_TEXT,
} from '@/utils/constants'
import { type MedicalRecordKind, useDeleteVisitMedicalRecord } from '../../hooks/useDeleteVisitMedicalRecord'
import { getDeleteMedicalRecordErrorMessage, isMedicalRecordNotFoundError } from './getDeleteMedicalRecordErrorMessage'

/** The record the admin picked to delete. `summary` is its author and date as shown in the list. */
export type DeleteMedicalRecordTarget = {
  kind: MedicalRecordKind
  medicalRecordId: string
  summary: string
}

type Props = {
  isOpen: boolean
  target: DeleteMedicalRecordTarget | null
  visitId: string
  onClose: () => void
  /** Called after a successful delete so the parent can refresh the visit view. */
  onDeleted: () => void
}

const COPY: Record<MedicalRecordKind, { title: string; confirmation: string; success: string }> = {
  note: {
    title: DELETE_NOTE_TITLE_TEXT,
    confirmation: DELETE_NOTE_CONFIRMATION_TEXT,
    success: NOTE_DELETED_TEXT,
  },
  'patient-instruction': {
    title: DELETE_PATIENT_INSTRUCTION_TITLE_TEXT,
    confirmation: DELETE_PATIENT_INSTRUCTION_CONFIRMATION_TEXT,
    success: PATIENT_INSTRUCTION_DELETED_TEXT,
  },
}

/**
 * Confirmation-with-reason dialog for deleting one visit note or patient instruction (QMD-4870).
 * Names the record being deleted, then asks for a required free-text reason; the Delete button is
 * disabled until the reason is non-empty. On success the parent refreshes the visit (and remarks,
 * since the backend adds an audit remark). If the record is already gone, the error is shown and
 * the visit is refreshed too, so the stale record leaves the list. Mirrors the DS `Popup` shape
 * used by EditPlanVisitsModal.
 */
export const DeleteMedicalRecordModal = ({ isOpen, target, visitId, onClose, onDeleted }: Props) => {
  const kind = target?.kind ?? 'note'
  const { title, confirmation, success } = COPY[kind]
  const [reason, setReason] = useState('')
  const { mutate, isPending } = useDeleteVisitMedicalRecord(kind)

  // DS Popup renders into a portal outside .tailwind-scoped — add the class so the Tailwind
  // utilities in the body resolve (mirrors EditPlanVisitsModal).
  useLayoutEffect(() => {
    const portalRoot = document.getElementById('headlessui-portal-root')
    if (portalRoot) portalRoot.classList.add('tailwind-scoped')
  }, [])

  // DS Popup stays mounted; reset the reason each time the modal opens for a clean slate.
  useEffect(() => {
    if (isOpen) setReason('')
  }, [isOpen])

  const canSubmit = target !== null && reason.trim().length > 0 && !isPending

  const handleConfirm = () => {
    if (!canSubmit) return
    mutate(
      { visitId, medicalRecordId: target.medicalRecordId, reason: reason.trim() },
      {
        onSuccess: () => {
          showToast({ type: 'success', message: success, position: 'bottom-right' })
          onClose()
          onDeleted()
        },
        onError: (error) => {
          captureException(error)
          showToast({ type: 'error', message: getDeleteMedicalRecordErrorMessage(error), position: 'bottom-right' })
          if (isMedicalRecordNotFoundError(error)) {
            onClose()
            onDeleted()
          }
        },
      },
    )
  }

  const body = (
    <div className='flex flex-col gap-sp-3'>
      <div className='flex items-center gap-sp-2'>
        <TriangleExclamationIcon iconSize='xl' className='text-error' />
        <Heading level='h2'>{title}</Heading>
      </div>
      {target ?
        <Paragraph size='sm' weight='semibold'>
          {target.summary}
        </Paragraph>
      : null}
      <Paragraph size='sm' className='text-text-medium'>
        {confirmation}
      </Paragraph>
      <Field>
        <FormLabel size='md'>{DELETE_MEDICAL_RECORD_REASON_LABEL_TEXT}</FormLabel>
        <Textarea
          className='resize-y'
          placeholder={TYPE_SOMETHING_TEXT}
          value={reason}
          disabled={isPending}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
        />
      </Field>
    </div>
  )

  const footer = (
    <div className='flex justify-end gap-sp-1.5'>
      <Button priority='secondary' onClick={onClose} disabled={isPending} size='m'>
        {CANCEL_TEXT}
      </Button>
      <Button theme='danger' onClick={handleConfirm} loading={isPending} disabled={!canSubmit} size='m'>
        {DELETE_TEXT}
      </Button>
    </div>
  )

  return (
    // lockOpen while deleting: Escape/backdrop must not close it mid-request (Cancel is disabled too).
    <Popup
      isOpen={isOpen}
      lockOpen={isPending}
      body={body}
      footer={footer}
      onClose={onClose}
      styles={{ containerClassName: 'w-[480px]' }}
    />
  )
}
